import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MessageCircle, Phone, Edit, CheckCircle, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SessionBookingWithMentee, updateSessionBookingStatus } from '@/utils/sessionBookingsUtils';
import { format, parseISO } from 'date-fns';
import { useDailyVideoCall } from '@/hooks/useDailyVideoCall';
import { useCallDurationManager } from '@/hooks/useCallDurationManager';
import { DailyCallManager } from '@/components/DailyCallManager';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { decrementBookingsUsed, dispatchSubscriptionBookingUpdate } from '@/utils/subscriptionBookingUtils';
import { getUserTimezone, convertFromUTC, getTimezoneAbbreviation } from '@/utils/timezoneUtils';

// Normalize course type string to a consistent label
function normalizeCourseType(type?: string | null): string | null {
  if (!type) return null;
  const t = String(type).trim().toLowerCase();
  if (t.includes('subscription')) return 'Subscription';
  if (['voicecall','voice call','video call','call','voice/video call','voice / video call'].includes(t)) return 'Voice/Video Call';
  return type;
}

interface AppointmentDetailsDialogProps {
  booking: SessionBookingWithMentee;
  isOpen: boolean;
  onClose: () => void;
  onBookingUpdated?: (bookingId: string, newStatus: string) => void;
}

const AppointmentDetailsDialog = ({ booking, isOpen, onClose, onBookingUpdated }: AppointmentDetailsDialogProps) => {
  const { findOrCreateVideoRoom, startVideoCall, isCreatingRoom } = useDailyVideoCall();
  const { isCallActive, callDuration, startCallTimer, endCallTimer } = useCallDurationManager();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userTimezone] = useState<string>(getUserTimezone());
  const [callInProgress, setCallInProgress] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [courseType, setCourseType] = useState<string | null>(normalizeCourseType(booking.course_type));
  const [currentTime, setCurrentTime] = useState(new Date());

  // Initialize course type and fetch if needed
  useEffect(() => {
    if (!isOpen) return;
    const normalized = normalizeCourseType(booking.course_type);
    if (normalized) {
      setCourseType(normalized);
    } else if (booking.application_id) {
      console.log('Fetching course type for booking:', booking.id, 'application_id:', booking.application_id);
      fetchCourseType();
    }
  }, [isOpen, booking.application_id, booking.course_type]);

  // Update current time every minute when dialog is open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isOpen]);

  const fetchCourseType = async () => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          courses (
            course_type
          )
        `)
        .eq('id', booking.application_id)
        .single();

      if (error) {
        console.error('Error fetching course type:', error);
        return;
      }

      if (data?.courses) {
        console.log('Course type fetched:', data.courses.course_type);
        setCourseType(normalizeCourseType(data.courses.course_type));
      } else {
        console.log('No course data found');
      }
    } catch (error) {
      console.error('Error in fetchCourseType:', error);
    }
  };

  // Helper function to determine if message button should be shown
  const shouldShowMessageButton = () => {
    return courseType === 'Subscription';
  };

  const handleMessageMentee = async () => {
    if (!user) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to send messages.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if conversation already exists
      const { data: existingConversation, error: fetchError } = await supabase
        .from('conversations')
        .select('id')
        .eq('mentor_id', user.id)
        .eq('mentee_id', booking.mentee_id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error checking existing conversation:', fetchError);
        toast({
          title: "Error",
          description: "Failed to check existing conversation. Please try again.",
          variant: "destructive",
        });
        return;
      }

      let conversationId = existingConversation?.id;

      // If no conversation exists, create one
      if (!conversationId) {
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            mentor_id: user.id,
            mentee_id: booking.mentee_id,
          })
          .select('id')
          .single();

        if (createError) {
          console.error('Error creating conversation:', createError);
          toast({
            title: "Error",
            description: "Failed to create conversation. Please try again.",
            variant: "destructive",
          });
          return;
        }

        conversationId = newConversation.id;
      }

      // Navigate to the conversation
      navigate(`/messages/${conversationId}`);
      onClose();
    } catch (error) {
      console.error('Error in handleMessageMentee:', error);
      toast({
        title: "Error",
        description: "Failed to start conversation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStartCall = async () => {
    try {
      setCallInProgress(true);
      
      console.log('Starting video call for booking:', booking.id);
      
      // Find or create Daily.co room using the new shared room logic
      const sessionData = await findOrCreateVideoRoom({
        mentorId: booking.mentor_id,
        menteeId: booking.mentee_id,
        bookingId: booking.id
      });

      if (sessionData) {
        // Start the call duration timer
        await startCallTimer(booking.mentor_id, booking.mentee_id, booking.booked_date);
        
        // Open the video call
        startVideoCall(sessionData.room_url, () => {
          console.log('Call started, duration tracking active');
        });
        
        toast({
          title: "Video Call Started",
          description: "The video call has been opened in a new window.",
        });
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      toast({
        title: "Error",
        description: "Failed to start video call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCallInProgress(false);
    }
  };

  const handleMarkAsComplete = async () => {
    setIsCompleting(true);
    try {
      const result = await updateSessionBookingStatus(booking.id, 'completed');
      
      if (result.success) {
        toast({
          title: "Appointment Completed",
          description: "The appointment has been marked as complete. Reviews are now available.",
        });
        
        // Notify parent component of the status change
        if (onBookingUpdated) {
          onBookingUpdated(booking.id, 'completed');
        }
        
        // Close the dialog
        onClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to mark appointment as complete. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error marking appointment as complete:', error);
      toast({
        title: "Error",
        description: "Failed to mark appointment as complete. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCompleting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleRequestChange = async () => {
    try {
      // 1) Cancel this booking
      const result = await updateSessionBookingStatus(booking.id, 'cancelled');
      if (!result.success) throw new Error('Failed to update booking status');

      // 2) For non-subscription: revert application to bookingRequired
      //    For subscription: give the slot back by decrementing bookings_used for the correct week
      if (booking.application_id) {
        if (courseType === 'Subscription') {
          console.log('DEBUG: Request Change - using subscription_booking_id:', booking.subscription_booking_id);
          const dec = await decrementBookingsUsed(
            booking.application_id, 
            booking.subscription_booking_id, 
            booking.booked_date
          );
          if (!dec.success) {
            console.warn('DEBUG: decrementBookingsUsed failed:', dec.error);
          }
          // Notify lists to refresh subscription cards
          dispatchSubscriptionBookingUpdate(booking.application_id, booking.mentor_id, booking.mentee_id);
        } else {
          const { error: appError } = await supabase
            .from('applications')
            .update({ status: 'bookingRequired', updated_at: new Date().toISOString() })
            .eq('id', booking.application_id);
          if (appError) throw appError;
        }
      }

      // 3) Fetch mentor's profile to get their name for the notification
      const { data: mentorProfile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", booking.mentor_id)
        .single();

      // Construct display name with fallback
      const mentorDisplayName = mentorProfile?.first_name && mentorProfile?.last_name 
        ? `${mentorProfile.first_name} ${mentorProfile.last_name}`.trim()
        : "Your mentor";

      // 4) Notify the mentee
      const { error: notifError } = await supabase.from('notifications').insert({
        receiver_id: booking.mentee_id,
        type: 'booking_change_requested',
        content: `${mentorDisplayName} requested a change to your session. Please re-book a new time.`,
        link: '/mentee-dashboard'
      });
      if (notifError) {
        console.warn('Failed to create notification:', notifError);
      }

      // 4) Let parent update local state
      onBookingUpdated?.(booking.id, 'cancelled');

      // 5) Dispatch a global event so lists can refresh
      window.dispatchEvent(new CustomEvent('booking-change-requested', {
        detail: { bookingId: booking.id, applicationId: booking.application_id, mentorId: booking.mentor_id, menteeId: booking.mentee_id }
      }));

      toast({
        title: 'Change requested',
        description: 'The mentee has been notified to re-book this session.',
      });

      onClose();
    } catch (error) {
      console.error('Error requesting change:', error);
      toast({
        title: 'Error',
        description: 'Could not request a change. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleConfirm = async () => {
    try {
      const result = await updateSessionBookingStatus(booking.id, 'confirmed');
      if (!result.success) throw new Error('Failed to confirm booking');

      onBookingUpdated?.(booking.id, 'confirmed');
      window.dispatchEvent(new CustomEvent('booking-status-updated', {
        detail: { bookingId: booking.id, status: 'confirmed' }
      }));

      toast({ title: 'Booking confirmed', description: 'This session has been confirmed.' });
      onClose();
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast({ title: 'Error', description: 'Failed to confirm booking.', variant: 'destructive' });
    }
  };

  // Check if Mark as Complete button should be shown
  const shouldShowMarkComplete = () => {
    const result = (
      booking.status === 'confirmed' &&
      !!booking.application_id &&
      courseType === 'Voice/Video Call'
    );
    console.log('shouldShowMarkComplete check:', {
      bookingStatus: booking.status,
      courseType,
      hasApplication: !!booking.application_id,
      shouldShow: result
    });
    return result;
  };

  // Check if action buttons should be shown (hidden after end time)
  const shouldShowActionButtons = () => {
    // ✅ FIXED: Convert UTC booking time to local time
    try {
      const endLocal = convertFromUTC(
        booking.booked_date,  // UTC date
        booking.end_time,     // UTC time
        userTimezone
      );
      
      const now = currentTime;
      
      // Only show action buttons BEFORE the session has ended
      return now <= endLocal.localDate;
    } catch (error) {
      console.error('Error parsing appointment times for action buttons:', error);
      return true;
    }
  };

  // Check if call button should be enabled based on time
  const getCallButtonState = () => {
    if (booking.status !== 'confirmed') {
      return { enabled: false, tooltip: 'Appointment must be confirmed to start call' };
    }

    try {
      // ✅ FIXED: Convert UTC booking times to local timezone
      const startLocal = convertFromUTC(
        booking.booked_date,
        booking.start_time,
        userTimezone
      );

      const endLocal = convertFromUTC(
        booking.booked_date,
        booking.end_time,
        userTimezone
      );

      const now = currentTime;

      if (now < startLocal.localDate) {
        return { 
          enabled: false, 
          tooltip: `The call will be available at ${startLocal.localTime}` 
        };
      } else if (now > endLocal.localDate) {
        return { 
          enabled: false, 
          tooltip: `This call ended at ${endLocal.localTime}` 
        };
      } else {
        return { 
          enabled: true, 
          tooltip: 'Click to start your scheduled call' 
        };
      }
    } catch (error) {
      console.error('Error parsing appointment times:', error);
      return { enabled: false, tooltip: 'Invalid appointment time format' };
    }
  };

  console.log('Dialog render - booking status:', booking.status, 'courseType:', courseType);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-bold">
              <Calendar className="h-6 w-6 text-emu-blue" />
              Appointment Details
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Mentee Information - Compact horizontal layout */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={booking.mentee.avatar_url} />
                  <AvatarFallback className="text-sm">
                    {booking.mentee.first_name[0]}{booking.mentee.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {booking.mentee.first_name} {booking.mentee.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">Mentee</p>
                </div>
              </div>
              <Badge className={`${getStatusColor(booking.status)} text-xs px-2 py-1`}>
                {booking.status}
              </Badge>
            </div>

            {/* Date and Time Details - Card background */}
            {(() => {
              const bookingLocal = convertFromUTC(
                booking.booked_date,
                booking.start_time,
                userTimezone
              );

              const endLocal = convertFromUTC(
                booking.booked_date,
                booking.end_time,
                userTimezone
              );

              return (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Date</p>
                        <p className="text-sm font-medium">
                          {format(bookingLocal.localDate, 'EEEE, MMMM d, yyyy')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Time</p>
                        <p className="text-sm font-medium">
                          {bookingLocal.localTime} - {endLocal.localTime}
                          <Badge variant="secondary" className="text-xs ml-2">
                            {getTimezoneAbbreviation(userTimezone)}
                          </Badge>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Notes Section */}
            {booking.notes && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                <p className="text-gray-700 text-sm">{booking.notes}</p>
              </div>
            )}

            {/* Action Buttons - Evenly spaced and smaller */}
            <div className="flex justify-between items-center pt-4 border-t gap-3">
              <div className="flex gap-2">
                {shouldShowMessageButton() && (
                  <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={handleMessageMentee}>
                    <MessageCircle className="h-3 w-3 mr-2" />
                    Message Mentee
                  </Button>
                )}
                {shouldShowActionButtons() && booking.status === 'confirmed' && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1">
                          <HelpCircle className="h-3 w-3 text-gray-400" />
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8 px-3 text-xs"
                            onClick={handleStartCall}
                            disabled={isCreatingRoom || callInProgress || !getCallButtonState().enabled}
                          >
                            <Phone className="h-3 w-3 mr-2" />
                            {isCreatingRoom || callInProgress ? 'Starting Call...' : 'Start Call'}
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">{getCallButtonState().tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              
              <div className="flex gap-2">
                {shouldShowActionButtons() && (
                  <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={handleRequestChange}>
                    <Edit className="h-3 w-3 mr-2" />
                    Request Change
                  </Button>
                )}
                {shouldShowMarkComplete() && (
                  <Button 
                    size="sm" 
                    className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={isCompleting}
                  >
                    <CheckCircle className="h-3 w-3 mr-2" />
                    {isCompleting ? 'Completing...' : 'Mark as Complete'}
                  </Button>
                )}
                {booking.status === 'pending' && (
                  <>
                    <Button size="sm" className="bg-emu-blue hover:bg-emu-blue/90 h-8 px-3 text-xs" onClick={handleConfirm}>
                      Confirm
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={onClose} size="sm" className="h-8 px-3 text-xs">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Appointment as Complete?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this appointment as complete? This action will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Move the appointment to your completed sessions</li>
                <li>Enable both you and the mentee to leave reviews</li>
                <li>Cannot be undone</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleMarkAsComplete}
              className="bg-green-600 hover:bg-green-700"
            >
              Yes, Mark as Complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DailyCallManager
        isCallActive={isCallActive}
        expiryTimestamp={undefined}
        onCallEnd={endCallTimer}
      />
    </>
  );
};

export default AppointmentDetailsDialog;
